import {useAuthenticationContext} from "../../../../utils/auth/AuthenticationContext";
import {parseISO} from "date-fns";
import {toString as kendoToString} from "@progress/kendo-intl"
import {ScrollView} from "@progress/kendo-react-scrollview";
import {Link, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import * as icons from "@fortawesome/free-solid-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import "./UnreadCommentsComponent.scss";
import FlintButton from "../../../controls/FlintButton";
import {useFlintDialogContext} from "../dialogs/FlintDialogContextProvider";
import axios from "axios";
import {SetCommentReadPointerRequest} from "../../../../utils/auth/auth.models";
import {CommentNotificationDTO} from "../../../../utils/signalr/signalr.models";
import {ApiUrl_Orders_SetCommentReadPointer} from "../../../../utils/Endpoints";
import {useFlintNotificationContext} from "../notifications/FlintNotificationContext";
import {useState} from "react";

export default function UnreadCommentsComponent() {
    const {lng} = useParams<{lng:string}>();
    const {comments, activePage, handlers} = useUnreadCommentsComponent();
    const {t} = useTranslation();
    //const {lng} = useParams<{ lng: string }>()
    if (comments.get.length == 0)
        return <div className={"w-100 px-3"}>
            <div className={"mb-2 text-center"}>
                no new comments
            </div>
            <FlintButton
                onClick={handlers.handleUnhideComments}
                className={"w-100"}
                fillMode={"outline"}
                themeColor={"warning"}
                title={t("COMMENTS_HISTORY_RecoverHidden_Hint")}>
                {t("COMMENTS_HISTORY_RecoverHidden")}
            </FlintButton>
        </div>
    return (
        <>
            <>{t("COMMENTS_HISTORY_Header")}</>
            <div id={"unread-comments"} className={"px-3 w-100"}>
                <ScrollView
                    activeView={activePage}
                    arrows={true}
                    endless={false}
                    pageable={false}
                    //pagerOverlay={"dark"}
                    automaticViewChange={false}
                    style={{width: '100%', height: 500}}>
                    {comments.get.map((c) => (
                        <div data-item-id={c.Id} key={c.Id} className={"unread-comment-item form-group comment-card"}>
                            <div className={"text-bold font-size-md mb-1 color-info"}>#{c.Id}</div>
                            <div className={"alert alert-light text-wrap overflow-auto comment-text"}
                                 dangerouslySetInnerHTML={{__html: c.Text}}></div>
                            <div>
                                <div className={"d-flex justify-content-center gap-2 font-size-md"}>
                                    <div>{t("orderNumber")}</div>
                                    <div>/</div>
                                    <div>
                                        <Link className={"me-2"}
                                              to={`/${lng}/orders/preview/${c.OrderGuid}`}>{c.OrderFullNumber}</Link>
                                        <Link to={`/${lng}/orders/preview/${c.OrderGuid}`} target="_blank">
                                            <FontAwesomeIcon icon={icons.faExternalLink}/>
                                        </Link>
                                    </div>
                                </div>
                                <div className={"d-flex justify-content-center gap-2  font-size-md"}>
                                    <div>{c.AuthorFullName}</div>
                                    <div>/</div>
                                    <div>{kendoToString(parseISO(c.CreatedOnUtc?.toString() + 'Z'), 'yyyy-MM-dd HH:mm')}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </ScrollView>
                <div className={"vstack gap-3"}>
                    <FlintButton
                        className={"w-100 mb-4"}
                        themeColor={"primary"}
                        onClick={handlers.handleHideToCurrent}
                        title={t("COMMENTS_HISTORY_HideOlder_Hint")}>
                        {t("COMMENTS_HISTORY_HideOlder")}
                    </FlintButton>
                    <FlintButton
                        onClick={handlers.handleHideAll}
                        className={"w-100"}
                        themeColor={"error"}
                        fillMode={"outline"}
                        title={t("COMMENTS_HISTORY_HideAll_Hint")}>
                        {t("COMMENTS_HISTORY_HideAll")}
                    </FlintButton>
                    <FlintButton
                        onClick={handlers.handleUnhideComments}
                        className={"w-100"}
                        fillMode={"outline"}
                        themeColor={"warning"}
                        title={t("COMMENTS_HISTORY_RecoverHidden_Hint")}>
                        {t("COMMENTS_HISTORY_RecoverHidden")}
                    </FlintButton>
                </div>
            </div>
        </>
    )
}

function useUnreadCommentsComponent() {
    const {comments, rootModel} = useAuthenticationContext()
    const {showAreYouSureDialog} = useFlintDialogContext();
    const {showNotification} = useFlintNotificationContext();
    const [activePage, setActivePage] = useState<number>(1);
    function getSelectedComment(): CommentNotificationDTO | null {
        let idData = document.querySelector("#unread-comments [aria-hidden='false'] .unread-comment-item")?.getAttribute("data-item-id");
        if (!idData)
            return null;
        const comment = comments.get.find((c) => c.Id === parseInt(idData!));
        return comment ?? null;
    }


    /** marks older elements (including current one) as read */
    async function handleHideToCurrent() {
        const lastCommentToHide = getSelectedComment();
        if (!lastCommentToHide)
            return;
        const index = comments.get.indexOf(lastCommentToHide);
        if (index < 0)
            return;
        if (index > 0) {
            const question = <span>You are about to mark <b>{index + 1}</b> comments as read.<br/>Are you sure you want to continue?</span>;
            return showAreYouSureDialog({
                content: question,
                onAccept: async () => {
                    await comments.hideOlderComments(lastCommentToHide)
                    setActivePage(1);
                    showNotification("Success!", {style: "success"})
                }
            })
        }
        await comments.hideOlderComments(lastCommentToHide)
        showNotification("Success!", {style: "success"})
    }

    /** marks all elements (including current one) as read*/
    function handleHideAll() {
        const lastCommentToHide = comments.get[comments.get.length - 1];
        const question = <span className={"color-flint-red"}>You are about to mark ALL <b>{comments.get.length}</b> comments as read.<br/>Are you sure you want to continue?</span>;
        showAreYouSureDialog({
            content: question,
            onAccept: async () => {
                await comments.hideOlderComments(lastCommentToHide)
                setActivePage(1);
                showNotification("Success!", {style: "success"})
            }
        })
    }

    /** opens a dialog allowing answering current comment */
    async function handleUnhideComments() {
        try {
            var result = await comments.unhideHiddenComments();
            setActivePage(10);
            showNotification(`Success! ${result} items has been recovered!`, {style: "success"})
        } catch (error: any) {
            showNotification(error);
        }
    }

    return {
        comments: {get: comments.get},
        activePage,
        handlers: {
            handleHideToCurrent,
            handleHideAll,
            handleUnhideComments
        }
    }
}